/* Right-side cart for Expanse. The footer stays mounted for Navidium app embeds. */
(() => {
  const root = document.getElementById('ScottBarnesCartDrawer');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const panel = root.querySelector('[role="dialog"]');
  const body = root.querySelector('[data-drawer-body]');
  const form = body.querySelector('form');
  const products = form.querySelector('[data-products]');
  const errorBox = root.querySelector('[data-drawer-error]');
  const status = root.querySelector('[data-drawer-status]');
  const routes = window.theme.routes;
  const focusSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let previousFocus;
  let background = [];
  let scrollY = 0;
  let savedBodyStyle;
  let refreshTimer;
  let refreshPromise;
  let refreshAgain = false;
  let changing = false;
  let stale = false;
  let pendingOpen = false;
  let addSource;
  let focusAfterRefresh;
  const disabledByDrawer = new Set();
  const localRequests = new WeakSet();

  const isOpen = () => root.classList.contains('is-active');
  const visibleFocusables = () => [...panel.querySelectorAll(focusSelector)].filter(el => el.getClientRects().length && !el.closest('[inert]'));
  const setTriggers = open => document.querySelectorAll('#HeaderCartTrigger, [data-cart-drawer-trigger]').forEach(el => el.setAttribute('aria-expanded', String(open)));
  const showError = message => {
    errorBox.querySelector('[data-drawer-error-message]').textContent = message;
    errorBox.hidden = false;
  };
  const clearError = () => { errorBox.hidden = true; };

  function busy() {
    const loading = changing || Boolean(refreshPromise) || Boolean(refreshTimer);
    form.setAttribute('aria-busy', String(loading));
    form.querySelectorAll('.js-qty__adjust, .js-qty__num, [name="checkout"]').forEach(el => {
      const shouldDisable = loading || (stale && el.matches('[name="checkout"]'));
      if (shouldDisable && !el.disabled) {
        disabledByDrawer.add(el);
        el.disabled = true;
      } else if (!shouldDisable && disabledByDrawer.has(el)) {
        el.disabled = false;
        disabledByDrawer.delete(el);
      }
    });
    for (const el of disabledByDrawer) if (!el.isConnected) disabledByDrawer.delete(el);
  }

  function open(source, shouldRefresh = true) {
    if (!isOpen()) {
      previousFocus = source instanceof HTMLElement ? source : document.activeElement;
      root.inert = false;
      root.setAttribute('aria-hidden', 'false');
      root.classList.add('is-active');
      scrollY = window.scrollY;
      savedBodyStyle = ['position', 'top', 'width'].map(key => [key, document.body.style[key]]);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.documentElement.classList.add('sb-cart-open');
      panel.querySelector('[data-cart-drawer-close]').focus({ preventScroll: true });
      // The drawer is a direct body child, outside the header's transformed layers.
      background = [...document.body.children].filter(el => el !== root && !['SCRIPT', 'STYLE', 'LINK'].includes(el.tagName)).map(el => [el, el.inert]);
      background.forEach(([el]) => { el.inert = true; });
      document.dispatchEvent(new CustomEvent('CartDrawer:open'));
      document.dispatchEvent(new CustomEvent('drawerOpen'));
    }
    setTriggers(true);
    if (shouldRefresh) scheduleRefresh(false);
  }

  function close() {
    if (!isOpen()) return;
    root.classList.remove('is-active');
    background.forEach(([el, wasInert]) => { el.inert = wasInert; });
    background = [];
    document.documentElement.classList.remove('sb-cart-open');
    savedBodyStyle.forEach(([key, value]) => { document.body.style[key] = value; });
    window.scrollTo({ top: scrollY, behavior: 'instant' });
    setTriggers(false);
    const target = previousFocus && previousFocus.isConnected && !previousFocus.closest('[inert]') && previousFocus.getClientRects().length ? previousFocus : document.getElementById('HeaderCartTrigger');
    if (target) target.focus({ preventScroll: true });
    root.inert = true;
    root.setAttribute('aria-hidden', 'true');
    document.dispatchEvent(new CustomEvent('CartDrawer:close'));
  }

  function render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = doc.querySelector('.cart__items');
    const discounts = doc.querySelector('.cart__discounts');
    if (!items || !discounts) throw new Error('Your bag could not be loaded. Please try again or view your cart.');
    const count = Number(items.dataset.count);
    const subtotal = Number(items.dataset.cartSubtotal);
    if (!Number.isFinite(count) || !Number.isFinite(subtotal)) throw new Error('Your bag could not be loaded. Please view your cart.');
    items.querySelectorAll('[id]').forEach(el => { el.id = 'Drawer-' + el.id; });
    items.querySelectorAll('label[for]').forEach(el => { el.htmlFor = 'Drawer-' + el.htmlFor; });
    const focusedRow = document.activeElement.closest('.cart__item');
    if (!focusAfterRefresh && focusedRow && products.contains(focusedRow)) {
      const focused = document.activeElement;
      const action = focused.matches('.js-qty__num') ? '.js-qty__num' : focused.matches('.js-qty__adjust--plus') ? '.js-qty__adjust--plus' : focused.matches('.js-qty__adjust--minus') ? '.js-qty__adjust--minus' : '.cart__remove a';
      focusAfterRefresh = { key: focusedRow.dataset.key, action };
    }
    products.replaceChildren(items);
    form.querySelector('[data-discounts]').replaceChildren(discounts);
    form.querySelector('[data-subtotal]').innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
    body.classList.toggle('is-empty', count === 0);
    root.querySelector('[data-drawer-count]').textContent = count;
    document.querySelectorAll('.cart-link__bubble-num').forEach(el => { el.textContent = count; });
    document.querySelectorAll('.cart-link__bubble').forEach(el => el.classList.toggle('cart-link__bubble--visible', count > 0));
    stale = false;
    status.textContent = count ? `Your bag has ${count} ${count === 1 ? 'item' : 'items'}.` : theme.strings.cartEmpty;
    // Cart page and drawer use different IDs, and keep independent item markup.
    const pageForm = document.getElementById('CartPageForm');
    if (pageForm && window.ScottBarnesCartDrawer) document.dispatchEvent(new CustomEvent('cart:drawer-rendered'));
  }

  async function refresh() {
    if (refreshPromise) {
      refreshAgain = true;
      return refreshPromise;
    }
    refreshPromise = (async () => {
      do {
        refreshAgain = false;
        try {
          const url = new URL(routes.cartPage, location.origin);
          url.searchParams.set('view', 'ajax');
          url.searchParams.set('_', Date.now());
          const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw new Error('Your bag could not be loaded. Please try again or view your cart.');
          render(await response.text());
        } catch (error) {
          stale = true;
          showError(error.message || 'Your bag could not be loaded. Please view your cart.');
        }
      } while (refreshAgain);
    })();
    busy();
    await refreshPromise;
    refreshPromise = null;
    busy();
    if (focusAfterRefresh && isOpen() && !changing) restoreItemFocus();
  }

  function restoreItemFocus() {
    if (focusAfterRefresh && isOpen()) {
      const { key, action } = focusAfterRefresh;
      const row = [...products.querySelectorAll('[data-key]')].find(el => el.dataset.key === key);
      const target = row && row.querySelector(action);
      (target || panel.querySelector('[data-cart-drawer-close]')).focus({ preventScroll: true });
    }
    focusAfterRefresh = null;
  }

  function scheduleRefresh(shouldOpen) {
    pendingOpen = pendingOpen || shouldOpen;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      if (pendingOpen) open(addSource, false);
      pendingOpen = false;
      addSource = null;
      refresh();
    }, 100);
    busy();
  }

  async function changeItem(row, quantity, action) {
    if (changing || refreshPromise || refreshTimer) return;
    const input = row.querySelector('.js-qty__num');
    const oldQuantity = Number(input.defaultValue);
    if (!Number.isInteger(quantity) || quantity < 0) {
      input.value = oldQuantity;
      showError('Please enter a whole-number quantity of zero or more.');
      return;
    }
    changing = true;
    clearError();
    focusAfterRefresh = { key: row.dataset.key, action };
    busy();
    try {
      const options = {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: row.dataset.key, quantity })
      };
      localRequests.add(options);
      const response = await fetch(routes.cartChange, options);
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'This quantity is unavailable.');
    } catch (error) {
      input.value = oldQuantity;
      showError(error.message || 'We could not update your bag. Please try again.');
    } finally {
      // Read the server state even after a lost response; do not guess whether a mutation applied.
      clearTimeout(refreshTimer);
      refreshTimer = null;
      await refresh();
      changing = false;
      busy();
      restoreItemFocus();
    }
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('#HeaderCartTrigger, [data-cart-drawer-trigger]');
    if (trigger && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      clearError();
      open(trigger);
      return;
    }
    if (!root.contains(event.target)) return;
    if (event.target.closest('[data-cart-drawer-close]')) { event.preventDefault(); close(); return; }
    const remove = event.target.closest('.cart__remove a');
    const button = event.target.closest('.js-qty__adjust');
    if (!remove && !button) return;
    const row = event.target.closest('.cart__item');
    if (!row) return;
    event.preventDefault();
    const input = row.querySelector('.js-qty__num');
    const minus = button && button.classList.contains('js-qty__adjust--minus');
    const quantity = remove ? 0 : Math.max(0, Number(input.value) + (minus ? -1 : 1));
    changeItem(row, quantity, remove ? '.cart__remove a' : minus ? '.js-qty__adjust--minus' : '.js-qty__adjust--plus');
  });
  form.addEventListener('change', event => {
    if (event.target.matches('.js-qty__num')) changeItem(event.target.closest('.cart__item'), Number(event.target.value), '.js-qty__num');
  });
  form.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('.js-qty__num')) {
      event.preventDefault();
      changeItem(event.target.closest('.cart__item'), Number(event.target.value), '.js-qty__num');
    }
  });
  form.addEventListener('submit', event => {
    const terms = form.querySelector('.cart__terms-checkbox');
    if (changing || refreshPromise || refreshTimer || stale || (terms && !terms.checked)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (terms && !terms.checked) { showError(theme.strings.cartTermsConfirmation); terms.focus(); }
    }
  }, true);
  const note = form.querySelector('[name="note"]');
  const noteButton = form.querySelector('.add-note');
  if (noteButton) noteButton.addEventListener('click', () => {
    noteButton.classList.toggle('is-active');
    note.classList.toggle('hide');
    if (!note.classList.contains('hide')) note.focus();
  });
  // Notes are submitted by the native cart form together with checkout.

  document.addEventListener('keydown', event => {
    if (!isOpen()) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const focusables = visibleFocusables();
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first) { event.preventDefault(); panel.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  });
  document.addEventListener('cart:open', () => open());
  document.addEventListener('cart:close', close);
  document.addEventListener('cart:build', () => scheduleRefresh(false));
  document.addEventListener('cart:updated', () => scheduleRefresh(false));
  document.addEventListener('ajaxProduct:added', event => {
    if (!isOpen() && event.detail) addSource = event.detail.addToCartBtn;
    scheduleRefresh(true);
  });
  document.addEventListener('MobileNav:open', close);
  document.addEventListener('modalOpen', close);

  // Observe successful cart writes from PageFly and app embeds as well as Expanse.
  // Wrappers preserve request arguments, response bodies and any existing app wrappers.
  function cartAction(value, method) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || String(method).toUpperCase() === 'GET') return null;
      return url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/)?.[1] || null;
    } catch (_) { return null; }
  }
  function written(action, data) {
    if (data && data.status >= 400) return;
    const items = data && (data.items || [data]);
    const merchandiseAdded = action === 'add' && items && items.some(item => item && !/navidium|shipping.protection/i.test(`${item.handle || ''} ${item.product_type || ''} ${item.title || ''}`));
    scheduleRefresh(Boolean(merchandiseAdded));
  }
  const originalFetch = window.fetch;
  window.fetch = function(input, options) {
    const action = options && localRequests.has(options) ? null : cartAction(typeof input === 'string' || input instanceof URL ? input : input.url, (options && options.method) || (input && input.method) || 'GET');
    const request = originalFetch.apply(this, arguments);
    if (action) request.then(response => {
      if (response.ok) response.clone().json().then(data => written(action, data)).catch(() => scheduleRefresh(false));
    }, () => {});
    return request;
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrActions = new WeakMap();
  XMLHttpRequest.prototype.open = function(method, url) {
    xhrActions.set(this, cartAction(url, method));
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function() {
    const action = xhrActions.get(this);
    if (action) this.addEventListener('load', () => {
      if (this.status < 200 || this.status >= 300) return;
      try { written(action, this.responseType === 'json' ? this.response : JSON.parse(this.responseText)); }
      catch (_) { scheduleRefresh(false); }
    }, { once: true });
    return xhrSend.apply(this, arguments);
  };

  const recommendations = root.querySelector('.cart-recommendations');
  if (recommendations) {
    new theme.QuickAdd(recommendations);
    new theme.QuickShop(recommendations);
  }
  window.ScottBarnesCartDrawer = { open, close, refresh: () => scheduleRefresh(false) };
})();
